// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { VaccineOutputs } from '../lib/models';

interface KPIExplanationModalProps {
  kpiType: string | null;
  model: VaccineOutputs;
  onClose: () => void;
}

export const KPIExplanationModal: React.FC<KPIExplanationModalProps> = ({
  kpiType,
  model,
  onClose,
}) => {
  if (!kpiType) return null;

  const getExplanation = () => {
    switch (kpiType) {
      case 'icer':
        return {
          title: 'ICER (Incremental Cost-Effectiveness Ratio)',
          description: 'The additional cost per additional QALY gained from the vaccine programme compared to no vaccination.',
          formula: 'ICER = ΔCost / ΔQALYs',
          calculation: `ICER = €${Math.round(model.incremental.costDifference).toLocaleString('fi-FI')} / ${Math.round(model.incremental.qalyGained).toLocaleString('fi-FI')} QALYs = €${Math.round(model.icer).toLocaleString('fi-FI')} per QALY`,
        };
      case 'qalysGained':
        return {
          title: 'QALYs Gained',
          description: 'Quality-Adjusted Life Years gained from the vaccine programme. QALYs combine quantity and quality of life into a single metric.',
          formula: 'ΔQALYs = QALYs (Vaccinate) - QALYs (No Vaccinate)',
          calculation: `ΔQALYs = ${Math.round(model.vaccinate.qalys).toLocaleString('fi-FI')} - ${Math.round(model.noVaccinate.qalys).toLocaleString('fi-FI')} = ${Math.round(model.incremental.qalyGained).toLocaleString('fi-FI')} QALYs`,
        };
      case 'budgetImpact':
        return {
          title: 'Budget Impact',
          description: 'The net budget increase or decrease from implementing the vaccine programme, accounting for programme costs and cost savings.',
          formula: 'Budget Impact = Programme Costs - Cost Savings',
          calculation: `Budget Impact = €${Math.round(model.vaccinate.programmeCost).toLocaleString('fi-FI')} - €${Math.round(model.noVaccinate.totalCost - model.vaccinate.healthcareCost).toLocaleString('fi-FI')} = €${Math.round(model.budgetImpact).toLocaleString('fi-FI')}`,
        };
      case 'bcr':
        return {
          title: 'BCR (Benefit-Cost Ratio)',
          description: 'The ratio of cost savings to programme costs. A BCR > 1 means the programme saves more than it costs.',
          formula: 'BCR = Cost Savings / Programme Cost',
          calculation: `BCR = €${Math.round(model.noVaccinate.totalCost - model.vaccinate.totalCost + model.vaccinate.programmeCost).toLocaleString('fi-FI')} / €${Math.round(model.vaccinate.programmeCost).toLocaleString('fi-FI')} = ${Math.round(model.bcr)}`,
        };
      case 'lifeYearsGained':
        return {
          title: 'Life-Years Gained',
          description: 'Additional years of life gained from preventing deaths through vaccination.',
          formula: 'ΔLife-Years = Life-Years (Vaccinate) - Life-Years (No Vaccinate)',
          calculation: `ΔLife-Years = ${model.vaccinate.lifeYears.toLocaleString('fi-FI')} - ${model.noVaccinate.lifeYears.toLocaleString('fi-FI')} = ${model.incremental.lifeYearsGained.toLocaleString('fi-FI')} life-years`,
        };
      case 'deathsAverted':
        return {
          title: 'Deaths Averted',
          description: 'Number of deaths prevented by the vaccine programme. Note: Deaths are assumed to occur mid-cycle, so each death loses 0.5 years (for a 1-year cycle).',
          formula: 'Deaths Averted = Deaths (No Vaccinate) - Deaths (Vaccinate)',
          calculation: `Deaths Averted = ${model.noVaccinate.deaths.toLocaleString('fi-FI')} - ${model.vaccinate.deaths.toLocaleString('fi-FI')} = ${model.incremental.deathsAverted.toLocaleString('fi-FI')} deaths`,
        };
      case 'casesAvoided':
        return {
          title: 'Cases Avoided',
          description: 'Number of disease cases prevented by the vaccine programme based on the user-defined inputs.',
          formula: 'Cases Avoided = Cases (No Vaccinate) - Cases (Vaccinate)',
          calculation: `Cases Avoided = ${Math.round(model.noVaccinate.cases).toLocaleString('fi-FI')} - ${Math.round(model.vaccinate.cases).toLocaleString('fi-FI')} = ${model.incremental.casesAvoided.toLocaleString('fi-FI')} cases`,
        };
      case 'hospitalizationsAvoided':
        return {
          title: 'Hospitalizations Avoided',
          description: 'Number of hospitalizations prevented by the vaccine programme.',
          formula: 'Hospitalizations Avoided = Hospitalizations (No Vaccinate) - Hospitalizations (Vaccinate)',
          calculation: `Hospitalizations Avoided = ${model.noVaccinate.hospitalizations.toLocaleString('fi-FI')} - ${model.vaccinate.hospitalizations.toLocaleString('fi-FI')} = ${model.incremental.hospitalizationsAvoided.toLocaleString('fi-FI')} hospitalizations`,
        };
      case 'adverseEventCost':
        return {
          title: 'Adverse Event Cost',
          description: 'Total cost of adverse events from vaccination.',
          formula: 'Adverse Event Cost = Vaccinated Population × Adverse Event Rate × Adverse Event Cost',
          calculation: `Adverse Event Cost = €${Math.round(model.costBreakdown.adverseEventCost).toLocaleString('fi-FI')}`,
        };
      case 'productivitySaved':
        return {
          title: 'Productivity Savings',
          description: 'Societal savings from avoided productivity losses due to prevented disease cases. This represents the economic value of work days saved when cases are prevented by vaccination.',
          formula: 'Productivity Savings = Cases Avoided × Productivity Loss Days × Daily Productivity Value',
          calculation: `Productivity Savings = €${Math.round(model.productivitySaved).toLocaleString('fi-FI')}`,
        };
      case 'noVax-qalys':
        return {
          title: 'QALYs (No Vaccinate Strategy)',
          description: 'Quality-Adjusted Life Years for the population without a vaccine programme. QALYs combine quantity and quality of life into a single metric.',
          formula: 'QALYs = Life Years × Quality of Life (utility)',
          calculation: `Total QALYs (No Vaccinate) = ${Math.round(model.noVaccinate.qalys).toLocaleString('fi-FI')} QALYs`,
        };
      case 'vax-qalys':
        return {
          title: 'QALYs (Vaccinate Strategy)',
          description: 'Quality-Adjusted Life Years for the population with a vaccine programme. QALYs combine quantity and quality of life into a single metric.',
          formula: 'QALYs = Life Years × Quality of Life (utility)',
          calculation: `Total QALYs (Vaccinate) = ${Math.round(model.vaccinate.qalys).toLocaleString('fi-FI')} QALYs`,
        };
      case 'noVax-lifeYears':
        return {
          title: 'Life-Years (No Vaccinate Strategy)',
          description: 'Total years of life for the population without a vaccine programme. Note: Deaths are assumed to occur mid-cycle, so each death loses 0.5 years (for a 1-year cycle).',
          formula: 'Life-Years = Population × Cycles - (Deaths × 0.5 years per death)',
          calculation: `Total Life-Years (No Vaccinate) = ${model.noVaccinate.lifeYears.toLocaleString('fi-FI')} years`,
        };
      case 'vax-lifeYears':
        return {
          title: 'Life-Years (Vaccinate Strategy)',
          description: 'Total years of life for the population with a vaccine programme. Note: Deaths are assumed to occur mid-cycle, so each death loses 0.5 years (for a 1-year cycle).',
          formula: 'Life-Years = Population × Cycles - (Deaths × 0.5 years per death)',
          calculation: `Total Life-Years (Vaccinate) = ${model.vaccinate.lifeYears.toLocaleString('fi-FI')} years`,
        };
      case 'noVax-deaths':
        return {
          title: 'Deaths (No Vaccinate Strategy)',
          description: 'Number of deaths from the disease without a vaccine programme.',
          formula: 'Deaths = Cases × Mortality Rate',
          calculation: `Total Deaths (No Vaccinate) = ${model.noVaccinate.deaths.toLocaleString('fi-FI')} deaths`,
        };
      case 'vax-deaths':
        return {
          title: 'Deaths (Vaccinate Strategy)',
          description: 'Number of deaths from the disease with a vaccine programme.',
          formula: 'Deaths = Cases × Mortality Rate',
          calculation: `Total Deaths (Vaccinate) = ${model.vaccinate.deaths.toLocaleString('fi-FI')} deaths`,
        };
      case 'noVax-cases':
        return {
          title: 'Cases (No Vaccinate Strategy)',
          description: 'Number of disease cases without a vaccine programme.',
          formula: 'Cases = Population × Incidence Rate',
          calculation: `Total Cases (No Vaccinate) = ${Math.round(model.noVaccinate.cases).toLocaleString('fi-FI')} cases`,
        };
      case 'vax-cases':
        return {
          title: 'Cases (Vaccinate Strategy)',
          description: 'Number of disease cases with a vaccine programme. Reduced by vaccine efficacy.',
          formula: 'Cases = Population × Incidence Rate × (1 - Vaccine Efficacy × Coverage)',
          calculation: `Total Cases (Vaccinate) = ${Math.round(model.vaccinate.cases).toLocaleString('fi-FI')} cases`,
        };
      case 'noVax-hospitalizations':
        return {
          title: 'Hospitalizations (No Vaccinate Strategy)',
          description: 'Number of hospitalizations from the disease without a vaccine programme.',
          formula: 'Hospitalizations = Cases × Hospitalization Rate',
          calculation: `Total Hospitalizations (No Vaccinate) = ${model.noVaccinate.hospitalizations.toLocaleString('fi-FI')} hospitalizations`,
        };
      case 'vax-hospitalizations':
        return {
          title: 'Hospitalizations (Vaccinate Strategy)',
          description: 'Number of hospitalizations from the disease with a vaccine programme.',
          formula: 'Hospitalizations = Cases × Hospitalization Rate',
          calculation: `Total Hospitalizations (Vaccinate) = ${model.vaccinate.hospitalizations.toLocaleString('fi-FI')} hospitalizations`,
        };
      case 'noVax-healthcareCost':
        return {
          title: 'Healthcare Costs (No Vaccinate Strategy)',
          description: 'Direct medical costs for treating disease cases without a vaccine programme.',
          formula: 'Healthcare Cost = Cases × Treatment Cost + Hospitalizations × Hospital Cost',
          calculation: `Healthcare Cost (No Vaccinate) = €${Math.round(model.noVaccinate.healthcareCost).toLocaleString('fi-FI')}`,
        };
      case 'vax-healthcareCost':
        return {
          title: 'Healthcare Costs (Vaccinate Strategy)',
          description: 'Direct medical costs for treating disease cases with a vaccine programme.',
          formula: 'Healthcare Cost = Cases × Treatment Cost + Hospitalizations × Hospital Cost',
          calculation: `Healthcare Cost (Vaccinate) = €${Math.round(model.vaccinate.healthcareCost).toLocaleString('fi-FI')}`,
        };
      case 'noVax-totalCost':
        return {
          title: 'Total Costs (No Vaccinate Strategy)',
          description: 'Total costs including healthcare and productivity losses without a vaccine programme.',
          formula: 'Total Cost = Healthcare Cost + Productivity Cost',
          calculation: `Total Cost (No Vaccinate) = €${Math.round(model.noVaccinate.totalCost).toLocaleString('fi-FI')}`,
        };
      case 'vax-totalCost':
        return {
          title: 'Total Costs (Vaccinate Strategy)',
          description: 'Total costs including programme delivery, healthcare, and productivity losses with a vaccine programme.',
          formula: 'Total Cost = Programme Cost + Healthcare Cost + Productivity Cost + Adverse Event Cost',
          calculation: `Total Cost (Vaccinate) = €${Math.round(model.vaccinate.totalCost).toLocaleString('fi-FI')}`,
        };
      case 'vax-programmeCost':
        return {
          title: 'Programme Cost (Vaccinate Strategy)',
          description: 'Cost of vaccine procurement and administration.',
          formula: 'Programme Cost = (Vaccine Cost + Admin Cost) × Doses × Coverage × Population',
          calculation: `Programme Cost = €${Math.round(model.vaccinate.programmeCost).toLocaleString('fi-FI')}`,
        };
      default:
        return null;
    }
  };

  const explanation = getExplanation();
  if (!explanation) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e293b',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '24px' }}>{explanation.title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>What it means:</h3>
          <p style={{ color: '#94a3b8', fontSize: '16px', lineHeight: '1.6', margin: 0 }}>
            {explanation.description}
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Formula:</h3>
          <code style={{ 
            display: 'block',
            backgroundColor: '#0f172a',
            color: '#10b981',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
          }}>
            {explanation.formula}
          </code>
        </div>

        <div>
          <h3 style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Calculation:</h3>
          <div style={{ 
            backgroundColor: '#0f172a',
            color: '#f1f5f9',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}>
            {explanation.calculation}
          </div>
        </div>
      </div>
    </div>
  );
};
